import React, { useState, useMemo, useEffect, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Grid,
    FormControl,
    InputLabel,
    Select,
    Typography,
    Box,
    useTheme,
    IconButton,
    Tooltip,
    useMediaQuery,
    Snackbar,
    Alert
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import API from "../api/axiosInstance";
import { TableField } from "../types/interfaces";

export type SearchFilterType =
    | "Equals"
    | "NotEquals"
    | "Contains"
    | "StartsWith"
    | "EndsWith"
    | "GreaterThan"
    | "LessThan"
    | "Between"
    | "In"
    | "NotIn"
    | "Empty"
    | "NotEmpty";

export interface FieldSearchFilter {
    fieldID: string;
    filterType: SearchFilterType;
    value?: string;
    value2?: string;
    values?: string[];
}

interface SavedFilter {
    savedFilterID: string;
    tableID: string;
    name: string;
    filterJson: string;
    deleted: boolean;
}

interface Props {
    open: boolean;
    onClose: () => void;
    fields: TableField[];
    onSubmit: (filters: FieldSearchFilter[]) => void;
    tableId?: string;
}

const SearchDialog: React.FC<Props> = ({ open, onClose, fields, onSubmit, tableId }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [filters, setFilters] = useState<Record<string, FieldSearchFilter>>({});
    const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
    const [newFilterName, setNewFilterName] = useState("");
    const [selectedSavedFilterId, setSelectedSavedFilterId] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({ open: false, message: "", severity: "success" });
    const formRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (open && tableId) {
            loadSavedFilters();
            setTimeout(() => formRef.current?.scrollTo({ top: 0 }), 100);
        }
    }, [open, tableId]);

    const loadSavedFilters = async () => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || "{}");
            const headers = {
              headers: {
                authToken: user.user?.authtoken,
                sessionId: user.user?.sessionId
              }
            };
            const res = await API.get(`/tables/${tableId}/saved-filters`,headers);
            setSavedFilters(res.data ?? []);
        } catch (err) {
            console.error("Failed to load saved filters:", err);
            setSnackbar({ open: true, message: "Failed to load saved filters.", severity: "error" });
        }
    };

    const handleFieldChange = (fieldId: string, changes: Partial<FieldSearchFilter>) => {
        setFilters(prev => ({
            ...prev,
            [fieldId]: {
                ...prev[fieldId],
                fieldID: fieldId,
                ...changes
            }
        }));
    };

    const clearAllFilters = () => {
        setFilters({});
    };

    const handleSubmit = () => {
        const compiled: FieldSearchFilter[] = Object.values(filters)
            .filter(f => f.filterType && (f.value || f.value2 || f.values || ["Empty", "NotEmpty"].includes(f.filterType)));
        onSubmit(compiled);
        onClose();
    };

    const handleSaveFilter = async () => {
        if (!tableId || !newFilterName.trim()) {
            setSnackbar({ open: true, message: "Please enter a name for the saved filter.", severity: "error" });
            return;
        }
        const safeName = newFilterName.trim();
        if (!/^[\w\s]{1,128}$/.test(safeName)) {
            setSnackbar({ open: true, message: "Filter name must be alphanumeric and max 128 characters.", severity: "error" });
            return;
        }
        if (savedFilters.some(f => f.name.toLowerCase() === safeName.toLowerCase() && !f.deleted)) {
            setSnackbar({ open: true, message: "A saved filter with this name already exists.", severity: "error" });
            return;
        }
        try {
            const user = JSON.parse(localStorage.getItem('user') || "{}");
            const headers = {
              headers: {
                authToken: user.user?.authtoken,
                sessionId: user.user?.sessionId
              }
            };
            const payload = {
                name: safeName,
                filterJson: JSON.stringify(Object.values(filters)),
            };
            await API.post(`/tables/${tableId}/saved-filters`, payload,headers);
            await loadSavedFilters();
            setNewFilterName("");
            setSnackbar({ open: true, message: "Filter saved successfully.", severity: "success" });
        } catch (err) {
            console.error("Save filter failed:", err);
            setSnackbar({ open: true, message: "Failed to save filter.", severity: "error" });
        }
    };

    const handleLoadSavedFilter = async (filterId: string) => {
        const filterToLoad = savedFilters.find(f => f.savedFilterID === filterId);
        if (!filterToLoad) {
            setSnackbar({ open: true, message: "Saved filter not found.", severity: "error" });
            return;
        }
        try {
            const parsedFilters = JSON.parse(filterToLoad.filterJson);
            const mapped: Record<string, FieldSearchFilter> = {};
            parsedFilters.forEach((f: FieldSearchFilter) => {
                mapped[f.fieldID] = f;
            });
            setFilters(mapped);
            setSelectedSavedFilterId(filterId);
            setSnackbar({ open: true, message: `Loaded saved filter: ${filterToLoad.name}`, severity: "success" });
        } catch (err) {
            console.error("Failed to parse saved filter:", err);
            setSnackbar({ open: true, message: "Failed to load saved filter.", severity: "error" });
        }
    };

    const handleDeleteSavedFilter = async (filterId: string) => {
        if (!window.confirm("Are you sure you want to delete this saved filter?")) return;
        try {
            await API.delete(`/tables/saved-filters/${filterId}`);
            await loadSavedFilters();
            if (selectedSavedFilterId === filterId) {
                setFilters({});
                setSelectedSavedFilterId(null);
            }
            setSnackbar({ open: true, message: "Filter deleted successfully.", severity: "success" });
        } catch (err) {
            console.error("Failed to delete saved filter:", err);
            setSnackbar({ open: true, message: "Failed to delete filter.", severity: "error" });
        }
    };

    const filterOptions: Record<string, SearchFilterType[]> = useMemo(() => ({
        string: ["Equals", "NotEquals", "Contains", "StartsWith", "EndsWith", "Empty", "NotEmpty"],
        number: ["Equals", "NotEquals", "GreaterThan", "LessThan", "Between", "Empty", "NotEmpty"],
        boolean: ["Equals", "NotEquals"],
        date: ["Equals", "GreaterThan", "LessThan", "Between", "Empty", "NotEmpty"],
        list: ["Equals", "NotEquals", "In", "NotIn", "Empty", "NotEmpty"]
    }), []);

    const getFilterOptionsForField = (field: TableField): SearchFilterType[] => {
        const type = (field.dataType || "").toLowerCase();
        if (field.listValues) return filterOptions.list;
        if (["int", "integer", "decimal", "float", "number"].includes(type)) return filterOptions.number;
        if (["bool", "boolean"].includes(type)) return filterOptions.boolean;
        if (["date", "datetime", "timestamp"].includes(type)) return filterOptions.date;
        return filterOptions.string;
    };

    const renderFilterInput = (field: TableField, filter: FieldSearchFilter | undefined) => {
        const type = (field.dataType || "").toLowerCase();
        const filterType = filter?.filterType;

        const commonProps = {
            fullWidth: true,
            size: "small" as const,
            margin: "dense" as const,
            InputLabelProps: { shrink: true }
        };

        if (!filterType || ["Empty", "NotEmpty"].includes(filterType)) return null;

        if (filterType === "Between") {
            return (
                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <TextField label="From" type={type.includes("date") ? "date" : "text"} value={filter?.value || ""} onChange={(e) => handleFieldChange(field.fieldID, { value: e.target.value })} {...commonProps} />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField label="To" type={type.includes("date") ? "date" : "text"} value={filter?.value2 || ""} onChange={(e) => handleFieldChange(field.fieldID, { value2: e.target.value })} {...commonProps} />
                    </Grid>
                </Grid>
            );
        }

        if (filterType === "In" || filterType === "NotIn") {
            return (
                <TextField label="Values (comma separated)" value={filter?.values?.join(", ") || ""} onChange={(e) => handleFieldChange(field.fieldID, { values: e.target.value.split(",").map((v) => v.trim()) })} {...commonProps} />
            );
        }

        if (type === "boolean") {
            return (
                <FormControl fullWidth margin="dense" size="small">
                    <InputLabel>Value</InputLabel>
                    <Select label="Value" value={filter?.value ?? ""} onChange={(e) => handleFieldChange(field.fieldID, { value: e.target.value })}>
                        <MenuItem value="true">True</MenuItem>
                        <MenuItem value="false">False</MenuItem>
                    </Select>
                </FormControl>
            );
        }

        return (
            <TextField label="Value" type={type.includes("date") ? "date" : "text"} value={filter?.value || ""} onChange={(e) => handleFieldChange(field.fieldID, { value: e.target.value })} {...commonProps} />
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="search-dialog-title" scroll="paper">
            <DialogTitle id="search-dialog-title">
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Search Filters</Typography>
                    <Box display="flex" gap={1}>
                        <TextField size="small" placeholder="Save filter name..." value={newFilterName} onChange={(e) => setNewFilterName(e.target.value)} />
                        <IconButton color="primary" onClick={handleSaveFilter}><SaveIcon /></IconButton>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent dividers ref={formRef}>
                {/* Saved Filters Management */}
                {savedFilters.length > 0 && (
                    <Box mb={3}>
                        <Typography fontWeight="bold" gutterBottom>Saved Filters</Typography>
                        <Grid container spacing={1}>
                            {savedFilters.filter(sf => !sf.deleted).map((filter) => (
                                <Grid item key={filter.savedFilterID}>
                                    <Button variant="outlined" size="small" onClick={() => handleLoadSavedFilter(filter.savedFilterID)}>
                                        {filter.name}
                                    </Button>
                                    <IconButton size="small" onClick={() => handleDeleteSavedFilter(filter.savedFilterID)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}

                {/* Fields Filters */}
                {fields.map((field) => {
                    const fieldFilter = filters[field.fieldID] || {};
                    const availableFilters = getFilterOptionsForField(field);
                    return (
                        <Box key={field.fieldID} mb={3}>
                            <Typography fontWeight="bold" gutterBottom>{field.fieldName}</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={5}>
                                    <FormControl fullWidth size="small" margin="dense">
                                        <InputLabel>Filter</InputLabel>
                                        <Select
                                            value={fieldFilter.filterType || ""}
                                            onChange={(e) =>
                                                handleFieldChange(field.fieldID, { filterType: e.target.value as SearchFilterType, value: "", value2: "", values: [] })
                                            }
                                            label="Filter"
                                        >
                                            {availableFilters.map((type) => (
                                                <MenuItem key={type} value={type}>{type}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={7}>
                                    {renderFilterInput(field, fieldFilter)}
                                </Grid>
                            </Grid>
                        </Box>
                    );
                })}
            </DialogContent>

            <DialogActions>
                <Button onClick={clearAllFilters} color="secondary">Clear</Button>
                <Button variant="contained" onClick={handleSubmit}>Search</Button>
                <Button onClick={onClose}>Cancel</Button>
            </DialogActions>

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
            </Snackbar>
        </Dialog>
    );
};

export default SearchDialog;
